import { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import api from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CaretLeft, CaretRight, Plus, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_DOT = { wartung: "bg-blue-500", pruefung: "bg-emerald-500", service: "bg-violet-500", notdienst: "bg-red-500" };
const WD = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function Kalender() {
  const [cursor, setCursor] = useState(dayjs().startOf("month"));
  const [appts, setAppts] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ title: "", type: "wartung", start: "", assignee: "" });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/calendar/appointments").then((r) => setAppts(r.data));
    api.get("/calendar/availability").then((r) => setAvailability(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = cursor.startOf("month");
  const firstDow = (start.day() + 6) % 7; // Montag = 0
  const daysInMonth = cursor.daysInMonth();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(cursor.date(d));

  const apptsOn = (day) => appts.filter((a) => a.start && dayjs(a.start).isSame(day, "day"));

  const create = async () => {
    if (!form.title || !form.start) return;
    try {
      await api.post("/calendar/appointments", {
        title: form.title, type: form.type,
        start: dayjs(form.start).toISOString(),
        assignee: form.assignee || null, source: "manual",
      });
      toast.success("Termin angelegt");
      setOpen(false); setForm({ title: "", type: "wartung", start: "", assignee: "" });
      load();
    } catch { toast.error("Anlegen fehlgeschlagen"); }
  };

  return (
    <div>
      <PageHeader title="Kalender" subtitle="Termine, Ressourcen & Verfügbarkeit (D-14) · automatisiert durch den Fälligkeits-Scheduler">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-2" data-testid="cal-create-btn"><Plus size={16} weight="bold" /> Termin</Button>
          </DialogTrigger>
          <DialogContent data-testid="cal-create-dialog">
            <DialogHeader><DialogTitle className="font-heading">Neuen Termin anlegen</DialogTitle></DialogHeader>
            <DialogDescription>Manueller Termin im Kalender (D-14).</DialogDescription>
            <div className="space-y-3">
              <Input data-testid="cal-title" placeholder="Titel" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="cal-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="wartung">Wartung</SelectItem>
                  <SelectItem value="pruefung">Prüfung</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="notdienst">Notdienst</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="cal-start" type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
              <Select value={form.assignee || "none"} onValueChange={(v) => setForm({ ...form, assignee: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="cal-assignee"><SelectValue placeholder="Monteur (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— unbestimmt —</SelectItem>
                  {availability.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button data-testid="cal-save" onClick={create} disabled={!form.title || !form.start}>Anlegen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_260px]">
        <Card className="rounded-md border p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold tracking-tight">{cursor.format("MMMM YYYY")}</h2>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" data-testid="cal-prev" onClick={() => setCursor(cursor.subtract(1, "month"))}><CaretLeft size={16} /></Button>
              <Button variant="outline" size="sm" onClick={() => setCursor(dayjs().startOf("month"))}>Heute</Button>
              <Button variant="outline" size="icon" className="h-8 w-8" data-testid="cal-next" onClick={() => setCursor(cursor.add(1, "month"))}><CaretRight size={16} /></Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {WD.map((w) => <div key={w} className="pb-2 text-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{w}</div>)}
            {cells.map((day, i) => (
              <button key={i} disabled={!day} onClick={() => day && setSelected(day)}
                data-testid={day ? `cal-day-${day.date()}` : undefined}
                className={`min-h-[68px] rounded-md border p-1.5 text-left transition-colors ${day ? "hover:border-primary/40" : "border-transparent"} ${day && day.isSame(dayjs(), "day") ? "border-primary bg-primary/5" : ""}`}>
                {day && <>
                  <div className="text-xs font-semibold">{day.date()}</div>
                  <div className="mt-1 flex flex-wrap gap-0.5">
                    {apptsOn(day).slice(0, 4).map((a) => <span key={a.id} className={`h-1.5 w-1.5 rounded-full ${TYPE_DOT[a.type] || "bg-primary"}`} />)}
                  </div>
                </>}
              </button>
            ))}
          </div>
        </Card>

        <Card className="rounded-md border p-4">
          <Label2>Verfügbarkeit heute</Label2>
          <div className="mt-3 space-y-2">
            {availability.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-sm">
                <span>{m.name}</span>
                <Badge variant="outline" className="rounded-md">{m.appointments} Termine</Badge>
              </div>
            ))}
            {availability.length === 0 && <p className="text-xs text-muted-foreground">Keine Monteure.</p>}
          </div>
        </Card>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="cal-day-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CalendarBlank size={20} weight="duotone" /> {selected?.format("dddd, D. MMMM YYYY")}</DialogTitle></DialogHeader>
          <div className="divide-y divide-border rounded-md border">
            {selected && apptsOn(selected).map((a) => (
              <div key={a.id} className="flex items-center gap-3 px-4 py-3">
                <span className={`h-2.5 w-2.5 rounded-full ${TYPE_DOT[a.type] || "bg-primary"}`} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{a.title}</div>
                  <div className="text-xs text-muted-foreground">{a.start?.slice(11, 16)} · {a.assignee || "unbestimmt"}</div>
                </div>
                <Badge variant="outline" className="rounded-md text-xs">{a.status}</Badge>
              </div>
            ))}
            {selected && apptsOn(selected).length === 0 && <div className="p-6 text-sm text-muted-foreground">Keine Termine.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
